import React, { useState } from 'react';

// In-app Help: the trip-purpose methodology (embedded) plus customer support.
// Opened from the ❓ button in the workspace header. The methodology PDF is
// served from /public (odinflow-methodology.pdf) for download.

const PURPLE = '#3D1A6E';
const TEAL = '#0D9488';
const MUTED = '#6B6880';
const RULE = '#EDE5F8';
const SUPPORT_EMAIL = 'support@odinflow.co.uk';

function H({ children }) {
  return <h3 style={{ fontSize: 15, fontWeight: 800, color: '#1C1C2E', margin: '26px 0 8px' }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontSize: 13.5, lineHeight: 1.62, color: '#3A3552', margin: '10px 0', maxWidth: '68ch' }}>{children}</p>;
}
function Num({ n }) {
  return <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, fontWeight: 700, color: PURPLE, border: `1px solid ${RULE}`, borderRadius: 5, padding: '2px 7px', marginRight: 10 }}>{n}</span>;
}
function Pill({ tone, children }) {
  const c = tone === 'edu' ? { bg: '#E4F5F1', fg: '#0D7A6B' } : tone === 'oth' ? { bg: '#F1EEF9', fg: MUTED } : { bg: '#F0EAFA', fg: PURPLE };
  return <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.fg, fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</span>;
}

function Table({ head, rows, firstColBold = true }) {
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${RULE}`, borderRadius: 10, margin: '18px 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
        <thead>
          <tr>{head.map((h, i) => (
            <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '9px 14px', background: '#F9F7FD', color: MUTED, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase', borderBottom: `1px solid ${RULE}` }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>{r.map((c, ci) => (
              <td key={ci} style={{ padding: '9px 14px', borderBottom: ri === rows.length - 1 ? 'none' : `1px solid ${RULE}`, textAlign: ci === 0 ? 'left' : 'right', fontWeight: ci === 0 && firstColBold ? 600 : 400, fontVariantNumeric: 'tabular-nums', fontFamily: ci === 0 ? 'inherit' : 'ui-monospace, Menlo, monospace', color: '#3A3552' }}>{c}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Formula({ children, where }) {
  return (
    <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14, background: '#F9F7FD', border: `1px solid ${RULE}`, borderLeft: `3px solid ${PURPLE}`, borderRadius: 8, padding: '14px 16px', margin: '16px 0', color: '#1C1C2E', overflowX: 'auto' }}>
      {children}
      {where && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 8 }}>{where}</div>}
    </div>
  );
}

function Note({ tone, children }) {
  const bg = tone === 'edu' ? '#E4F5F1' : '#F0EAFA';
  return <div style={{ background: bg, borderRadius: 8, padding: '12px 16px', margin: '18px 0', fontSize: 13, lineHeight: 1.55, color: '#3A3552' }}>{children}</div>;
}

function Methodology() {
  return (
    <div>
      <div style={{ fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.18em', textTransform: 'uppercase', color: PURPLE, fontWeight: 600 }}>Methodology Note · v1.0</div>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1C1C2E', margin: '10px 0 6px', letterSpacing: '-0.01em' }}>Trip Generation by Purpose &amp; Distribution</h2>
      <p style={{ fontSize: 15, color: MUTED, margin: '0 0 8px', maxWidth: '60ch' }}>How Odinflow splits a development's person trips into Employment, Education and Other, then distributes and mode-splits each purpose independently before network assignment.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 22px', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', color: MUTED, margin: '14px 0 4px' }}>
        <span>Scope: <strong style={{ color: '#1C1C2E' }}>England</strong></span>
        <span>Survey: <strong style={{ color: '#1C1C2E' }}>NTS 2024</strong></span>
        <span>Schools: <strong style={{ color: '#1C1C2E' }}>DfE GIAS</strong></span>
      </div>

      <a href="/odinflow-methodology.pdf" download
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '9px 16px', background: PURPLE, color: 'white', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
        ⬇ Download methodology (PDF)
      </a>

      <H><Num n="1" />Overview</H>
      <P>Conventional trip generation distributes and mode-splits every trip the same way — implicitly as if all travel were commuting. In reality a resident's commute, a child's journey to school, and a shopping trip share almost nothing in their geography or mode choice.</P>
      <P>Odinflow separates person trips into three purposes and carries each through its own distribution and mode share, recombining them only at network assignment. Employment and Other keep the established census journey-to-work distribution; Education is distributed to real schools by a gravity model.</P>

      <H><Num n="2" />Purpose classification</H>
      <P>Person trips are grouped into three purposes, mapping the National Travel Survey's categories onto the streams. The school run (escort education) is grouped with Education because it shares that geography and timing.</P>
      <Table head={['Stream', 'NTS purposes', 'Distribution']} rows={[
        [<Pill tone="emp">Employment</Pill>, 'Commuting, Business', 'Census journey-to-work'],
        [<Pill tone="edu">Education</Pill>, 'Education, Escort education', 'School gravity model'],
        [<Pill tone="oth">Other</Pill>, 'Shopping, personal business, leisure, visiting, holiday', 'Census (proxy)'],
      ]} firstColBold={false} />
      <Note>Other has no dedicated OD survey in the census, so it currently borrows the journey-to-work distribution as a proxy. Only Education is mechanically separated in this release; the three-way split is preserved so Other can take its own basis later.</Note>

      <H><Num n="3" />Purpose split by time period</H>
      <P>The split varies sharply through the day, so it is applied <strong>per time period</strong>, seeded from the NTS distribution of trips by start time (NTS0502a). Values are editable defaults. Each period can be seeded two ways:</P>
      <P><strong>Hour-specific</strong> — the single clock hour of the start time (captures the sharp 08:00 school-run peak). <strong>Broad peak</strong> — the sample-weighted average of the three hours centred on the start time (e.g. 07:00–09:59), smoothing the spike into a peak-window view.</P>
      <Table head={['Period basis', 'Emp', 'Edu', 'Other']} rows={[
        ['AM · 08:00 hour', '18.9', '53.4', '27.7'],
        ['AM · 07–10 broad', '23.7', '34.8', '41.5'],
        ['PM · 17:00 hour', '27.8', '7.0', '65.2'],
        ['PM · 16–19 broad', '23.3', '7.2', '69.5'],
      ]} />
      <P>The full 24-hour table (both bands) is built in, so any start time resolves to a seeded split. Edited splits are re-normalised to total 100%.</P>

      <H><Num n="4" />Distribution by purpose</H>
      <P><strong>Employment &amp; Other</strong> distribute across census destination zones using 2021 Census journey-to-work flows (Nomis NM_1228), tiered by distance from the site — fine LSOA detail locally, MSOA beyond.</P>
      <P><strong>Education</strong> is distributed to real schools from the DfE Get Information About Schools register (every open English school with a location and pupil roll) by a singly-constrained gravity model:</P>
      <Formula where={<>Wⱼ — pupils on roll (attraction) · dⱼ — distance site→school · β — distance decay</>}>Tⱼ ∝ Wⱼ · exp( −β · dⱼ )</Formula>
      <P>Primary and secondary run separately (their catchments differ greatly), each over its own school set with its own decay, then recombined per school. The demand split (56 / 44 primary/secondary) is the national pupil ratio; each β is calibrated to the NTS mean education trip length (mean ≈ 2/β).</P>
      <Table head={['Stream', 'Mean length', 'β / km', 'Demand']} rows={[
        ['Primary', '2.6 km', '0.77', '56%'],
        ['Secondary', '5.5 km', '0.36', '44%'],
      ]} />
      <Note tone="edu">Because supply drives the outcome, modelled trip lengths self-adjust: dense urban areas give short catchments (~2.4 km primary), sparse rural areas stretch to the nearest school (~5 km primary, ~8.6 km secondary) — the correct direction with no per-site tuning.</Note>

      <H><Num n="5" />Mode share by purpose</H>
      <P>Employment and Other take the site's census method-of-travel-to-work split (Nomis NM_1208), with manual/per-zone overrides. Education carries its own default — school travel is far more walked and cycled than commuting:</P>
      <Table head={['Car', 'Walk', 'Bus', 'Rail', 'Cycle', 'Other']} rows={[
        ['46.6', '40.5', '6.7', '1.7', '1.2', '3.3'],
      ]} firstColBold={false} />
      <P>Only the car share (46.6%, vs a commute share well above 60%) converts education person trips into the vehicle trips that reach the network.</P>

      <H><Num n="6" />Assignment integration</H>
      <P>In each period the education fraction is removed from the census person-trip pool and routed separately. The non-education pool distributes across census zones at the census/manual car share (the existing assignment, scaled down); the education pool distributes to schools and converts to vehicles at the education car share.</P>
      <P>Both route on the road network in a single build, so education car trips contribute to link volumes and junction turning movements alongside every other trip. Per-destination vehicle counts use largest-remainder allocation, so rounded trips always sum exactly to target.</P>

      <H><Num n="7" />Data sources</H>
      <Table head={['Input', 'Source', 'Vintage']} rows={[
        ['Purpose split by time', 'DfT NTS0502a', '2024'],
        ['Education mode & length', 'DfT NTS0409', '2024'],
        ['Schools', 'DfE GIAS', 'Daily'],
        ['Emp/Other distribution', 'Census J2W (NM_1228)', '2021'],
        ['Emp/Other mode share', 'Census MTTW (NM_1208)', '2021'],
      ]} firstColBold={false} />

      <H><Num n="8" />Assumptions &amp; limitations</H>
      <P style={{ margin: '10px 0' }}>
        <strong>England only</strong> — GIAS and Nomis cover England; other nations need their own sources. <strong>Other as a commute proxy</strong> understates dispersed off-peak destinations. <strong>Straight-line decay</strong> — the gravity model uses great-circle distance, not routed time. <strong>Phase from age range</strong> — schools bucketed primary/secondary/all-through; the demand split is a national default. <strong>Editable throughout</strong> — every default is a national starting point to review against local evidence.
      </P>
    </div>
  );
}

function Support() {
  return (
    <div>
      <div style={{ fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.18em', textTransform: 'uppercase', color: TEAL, fontWeight: 600 }}>We're here to help</div>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1C1C2E', margin: '10px 0 6px' }}>Customer support</h2>
      <P>Questions about a project, a result you don't expect, or a feature request — get in touch and we'll help.</P>

      <div style={{ background: '#F9F7FD', border: `1px solid ${RULE}`, borderRadius: 12, padding: '20px 22px', margin: '20px 0' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>Email support</div>
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Odinflow support`} style={{ fontSize: 20, fontWeight: 800, color: PURPLE, textDecoration: 'none' }}>{SUPPORT_EMAIL}</a>
        <div style={{ fontSize: 12.5, color: MUTED, marginTop: 8 }}>We usually reply within <strong style={{ color: '#1C1C2E' }}>1 working day</strong>.</div>
      </div>

      <H>To help us help you</H>
      <P>When reporting an issue, please include:</P>
      <ul style={{ fontSize: 13.5, lineHeight: 1.6, color: '#3A3552', paddingLeft: 20, maxWidth: '66ch' }}>
        <li>The <strong>project name</strong> (and scenario, if relevant).</li>
        <li>What you did, what you expected, and what happened instead.</li>
        <li>A screenshot if it's a visual or map issue.</li>
      </ul>

      <H>Methodology &amp; data</H>
      <P>How trip generation, purpose split, distribution and assignment work — and the national datasets behind the defaults — are documented in the Methodology tab, downloadable as a PDF for your Transport Assessment appendices.</P>
    </div>
  );
}

export default function HelpModal({ onClose }) {
  const [tab, setTab] = useState('methodology');
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,46,0.45)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Nunito Sans, Avenir, sans-serif' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 14, width: 'min(760px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(28,20,46,0.35)', overflow: 'hidden' }}>
        {/* Header + tabs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${RULE}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ k: 'methodology', label: '📄 Methodology' }, { k: 'support', label: '💬 Support' }].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: tab === t.k ? PURPLE : '#F5F0FA', color: tab === t.k ? 'white' : PURPLE }}>
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={onClose} aria-label="Close help"
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${RULE}`, background: 'white', cursor: 'pointer', fontSize: 16, color: MUTED, lineHeight: 1 }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding: '24px 28px 32px', overflowY: 'auto' }}>
          {tab === 'methodology' ? <Methodology /> : <Support />}
        </div>
      </div>
    </div>
  );
}
